import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { Box } from "@mui/material";
import { useEffect } from "react";
import { calculateMarkdownDiff } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/calculateDiffMarkdown";
import { PLAYGROUND_TRANSFORMERS } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/markdownTransformers";
import { postProcessHtmlTagsInNodes } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/postProccessDiff";
import {
  convertHtmlTagsIntoMarkdownSyntaxOnly,
  flattenDataKeyReferencesToSnapshotValues,
  importMarkdownToLexical,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/preprocessMarkdown";
import { editorConfig } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { TableContext } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/tablePlugin/TablePlugin";
import { captureSentryException } from "src/utils/sentry";

interface DiffViewerProps {
  currentMarkdown: string;
  previousMarkdown: string;
  tableStyles?: Record<string, unknown>;
}

const DiffViewerInner = ({
  currentMarkdown,
  previousMarkdown = "",
  tableStyles,
}: DiffViewerProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(false);
  }, [editor]);

  // Set editor to read-only and import diff content on mount/updates
  useEffect(() => {
    editor.update(() => {
      // Process both current and previous versions for diff comparison
      // convertHtmlTagsIntoMarkdownSyntaxOnly handles <p></p> → marker conversion
      const currentMarkdownProcessed = convertHtmlTagsIntoMarkdownSyntaxOnly(
        flattenDataKeyReferencesToSnapshotValues(currentMarkdown)
      );
      let previousVersionProcessed = previousMarkdown;

      previousVersionProcessed = convertHtmlTagsIntoMarkdownSyntaxOnly(
        flattenDataKeyReferencesToSnapshotValues(previousVersionProcessed)
      );

      // Calculate the diff between versions
      let markdownToImport: string;
      try {
        const diffResult = calculateMarkdownDiff({
          currentContent: currentMarkdownProcessed,
          previousContent: previousVersionProcessed,
        });
        markdownToImport = diffResult.diffResultMarkdown;
      } catch (error) {
        captureSentryException(error);
        markdownToImport = currentMarkdownProcessed;
      }

      // Import the diff result using the shared helper
      // - skipHtmlConversion: diff result is already processed, don't convert again
      // - preserveEmptyParagraphs: true to clean up markers after import
      importMarkdownToLexical(markdownToImport, PLAYGROUND_TRANSFORMERS, {
        skipHtmlConversion: true,
        preserveEmptyParagraphs: true,
        postProcess: postProcessHtmlTagsInNodes,
      });
    });
  }, [editor, currentMarkdown, previousMarkdown]);

  return (
    <TableContext>
      <Box
        className="editor-container"
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
          position: "relative",
          width: "auto",
          padding: "0",
          height: "100%",

          "& .tableScrollableWrapper": {
            marginBottom: "16px",
          },

          "& .tableScrollableWrapper:last-child": {
            marginBottom: "0",
          },

          "& .editor-input": {
            height: "100%",
            padding: "16px",
          },

          "& > .editor-inner": {
            height: "fit-content",
          },
          "& > .editor-inner > .editor-input": {
            height: "fit-content",
            whiteSpace: "normal",
          },
          "& > .editor-inner > .editor-input > .editor-paragraph:not(:last-child)":
            {
              marginBottom: "16px",
            },
          "& > .editor-inner > .editor-input > .editor-paragraph > span:last-child":
            {
              paddingBottom: "0",
            },
          "& .editor-inner .editor-input table": {
            tableLayout: "auto",
            ...(tableStyles || {}),
            width: tableStyles?.width || "100%",
            maxWidth: tableStyles?.maxWidth || "100%",
            minWidth: "0",
            display: "block",
            overflowX: "auto",
            boxSizing: "border-box",
            border: "none",
            "& tr > td > ul": {
              padding: "inherit",
            },
            "& tr > td > ol": {
              padding: "inherit",
            },
          },
        }}
      >
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="editor-input outline-none" />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ListPlugin />
          <HistoryPlugin />
          <TablePlugin hasHorizontalScroll={true} />
        </div>
      </Box>
    </TableContext>
  );
};

const DiffViewer = (props: DiffViewerProps) => {
  return (
    <LexicalComposer
      initialConfig={{
        ...editorConfig,
        namespace: "diff-editor",
        editable: false,
      }}
    >
      <DiffViewerInner {...props} />
    </LexicalComposer>
  );
};

export default DiffViewer;
